/**
 * Shared motion constants and easing.
 *
 * Both apps drive the rotator from these, so the pacing is identical on web and
 * native rather than each inheriting its platform's default scroll curve —
 * `scrollTo({behavior:'smooth'})` and RN's `scrollTo({animated:true})` have
 * different durations and different curves, which is exactly the kind of thing
 * that makes two builds of one app feel like two apps.
 */

export const ROTATOR = {
  /** How long a slide is held before advancing. */
  holdMs: 5600,
  /** The slide transition itself. Long enough to read as deliberate. */
  slideMs: 720,
  /** One full pan cycle across a slide. Longer than the hold, so it never loops. */
  panMs: 9000,
  /** Ken Burns start and end scale. Small — this is drift, not a zoom. */
  panScaleFrom: 1.06,
  panScaleTo: 1.13,
  /** Horizontal drift as a fraction of frame width. */
  panDriftX: 0.022,
  panDriftY: 0.012,
  /** Text entrance, once the slide has mostly settled. */
  textMs: 460,
  /** Held until the slide is ~55% through, so copy never rides the movement. */
  textInDelayMs: 400,
  /** Exit is faster than entry and has no delay: the outgoing copy must be
   *  gone before the new card arrives, or the two read as a cross-fade. */
  textOutMs: 190,
  textRiseFrom: 14,
} as const;

/**
 * Ease in and out — slow to start, quick through the middle, settling at the
 * end. The stylised curve the rotator asks for; a linear slide reads as a
 * filmstrip being dragged.
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Decelerating curve for the pan, so drift slows as the slide settles. */
export function easeOutSine(t: number): number {
  return Math.sin((t * Math.PI) / 2);
}

export interface TweenHandle {
  cancel(): void;
}

/**
 * A cancellable rAF tween. Used instead of the platforms' built-in smooth
 * scrolling so both get the same curve and the same duration.
 */
export function tween(
  from: number,
  to: number,
  durationMs: number,
  onFrame: (value: number) => void,
  ease: (t: number) => number = easeInOutCubic,
  onDone?: () => void,
): TweenHandle {
  const start = Date.now();
  let raf = 0;
  let cancelled = false;

  const step = () => {
    if (cancelled) return;
    const elapsed = Date.now() - start;
    const t = Math.min(1, elapsed / durationMs);
    onFrame(from + (to - from) * ease(t));
    if (t < 1) {
      raf = requestAnimationFrame(step);
    } else {
      onDone?.();
    }
  };
  raf = requestAnimationFrame(step);

  return {
    cancel() {
      cancelled = true;
      cancelAnimationFrame(raf);
    },
  };
}
